import { useEffect, useState, FC } from 'react';
import { Skeleton, Typography } from '@mui/material';
import { Job, Rooftop } from '@libs/models';
import { useJob } from '@carcareers/hooks';
import { useForm } from '@carcareers/contexts';
import { AutocompleteInput } from '@carcareers/components';
import { jobApi } from '@carcareers/store';

interface JobSelectProps {
  name: string;
  rooftopName?: string;
  useRooftop?: boolean;
}

export const JobSelect: FC<JobSelectProps> = ({ name, rooftopName = 'rooftop', useRooftop = false }) => {
  const { indexJobs } = useJob();
  const { getData, setErrors, errors } = useForm<Job>();
  const [jobs, setJobs] = useState<Job[]>([]);
  const rooftop = getData<Rooftop>(rooftopName);
  
  const rooftopId = rooftop?.id || 0;
  const { data: rooftopJobs, isLoading: isLoadingRooftopJobs } = jobApi.useJobFindByRooftopQuery(
    { rooftopId, page: 1, limit: 100 }, 
    { skip: !useRooftop || !rooftop?.id }
  );

  useEffect(() => {
    if (useRooftop && rooftop?.id && !isLoadingRooftopJobs && rooftopJobs) {
      setJobs(rooftopJobs.items || []);
      // Clear errors when jobs are loaded for selected rooftop
      if (errors && errors.length > 0) {
        setErrors(prevErrors => prevErrors.filter(error => error.property !== name));
      }
    } else if (!useRooftop && !indexJobs.isLoading && indexJobs.data) {
      setJobs(indexJobs.data.items || []);
      // Clear errors when jobs are loaded
      if (errors && errors.length > 0) {
        setErrors(prevErrors => prevErrors.filter(error => error.property !== name));
      }
    }
  }, [indexJobs, rooftopJobs, isLoadingRooftopJobs, useRooftop, rooftop?.id, name]);

  if (useRooftop && !rooftopId) {
    return <Typography variant="subtitle2">Select a rooftop first</Typography>;
  }

  if (useRooftop && rooftopId && !jobs?.length) {
    return <Typography variant="subtitle2">No Jobs for this rooftop</Typography>;
  }

  return !jobs.length ? (
    <Skeleton variant="rounded" height={56} />
  ) : (
    <AutocompleteInput
      name={name}
      label="Job"
      options={jobs.map((job) => ({ label: `${job.id} - ${job.title}`, value: job }))}
    />
  );
};

export default JobSelect;
